import { I18nLang } from './user';
import { CountryCode } from './country';
import { AutoSearchMode } from './vehicleAutoSearch';
import { EmailServiceOptions } from './email-service';

export enum TenantNegotiationStrategy {
    ENFORCE_CUSTOMER_PRICE_OFFER = 0
}

export interface PluginConfig {
    TimocomMarketplace?: {
        username?: string;
        password?: string | null;
        timocomId?: string;
        contactPerson?: string;
        contactPhone?: string;
    };
    TransEUMarketplace?: {
        accessToken?: string | null;
        refreshToken?: string | null;
        tokenExpiresAt?: number;
    };
    TelerouteMarketplace?: {
        username?: string;
        password?: string | null;
    };
    CartrackTracker?: {
        username?: string;
        password?: string | null;
        host?: string;
    };
    SamsaraTracker?: {
        apiKey?: string | null;
    };
}

export type MarketplaceProvider = 'Timocom' | 'Wtransnet' | 'TransEU' | 'Teleroute' | 'LKWWalter';
export enum MarketplaceNameEnum {
    TimocomMarketplace = 'TimocomMarketplace',
    WtransnetMarketplace = 'WtransnetMarketplace',
    TransEUMarketplace = 'TransEUMarketplace',
    TelerouteMarketplace = 'TelerouteMarketplace',
    LKWWalterMarketplace = 'LKWWalterMarketplace',
    // OfferQueryResponse = 'OfferQueryResponse',
    // Manual = 'Manual'
}

export interface Destination {
    country: CountryCode;
    codes?: string[];
    score?: number;
    geoCoordinate?: Geography;
    stepsAhead?: number; // Number of steps ahead to search for offers (1 = next destination only, higher = search further ahead). Default: 1
};

export interface Geography {
    latitude: number;
    longitude: number;
}

export interface ScoringWeightConfig {
    readonly value: number;
    readonly label: string;
    readonly description: string;
}

export enum PriceSortPolicy {
    BY_ESTIMATED_PRICE = 0,
    BY_CLIENT_THEN_ESTIMATED_PRICE = 1,
    BY_MAX_PRICE = 2
}

export const PriceSortPolicyLabels: Record<number, string> = {
  [PriceSortPolicy.BY_ESTIMATED_PRICE]: 'By estimated price',
  [PriceSortPolicy.BY_CLIENT_THEN_ESTIMATED_PRICE]: 'By client price then estimated price (if client price not available)',
  [PriceSortPolicy.BY_MAX_PRICE]: 'By max price'
};

export interface Tenant extends EmailServiceOptions {
    id?: number;
    updatedOn?: Date;
    name: string;
    createdAt: Date;
    brandingName: string;
    brandingLogoUrl: string;
    apiRateLimitPerMinute: number;
    defaultLanguage: I18nLang;
    timezoneOffset: number;
    notificationEmail: string;
    allowUserSignup: boolean;
    dataRetentionDays: number;
    isActive: boolean;
    pricePerKm?: number;
    minLengthMForFullTruckLoad?: number;
    maxLoadingDaysAhead?: number; // Maximum number of days ahead for auto-search loading date range (default: 3)
    potentialOffersRadiusKm?: number; // Search radius in km for potentialOffers (default: 200km)
    destinationsToExclude: Destination[];
    preferredDestinations: { [id: string]: Destination[] };
    returnDestinations: { [id: string]: Destination[] };
    preferredRoutes: { [id: string]: Destination[] };
    preferredAutoSearchMode: AutoSearchMode;
    allowAutoValidate: boolean;
    includePlatesOnAutoValidate: boolean;
    targetPriceMarginFactor: number;
    regCountry: string; // In ISO2 format
    imapForwardingAddress: string;
    whitelistedDomains: string[];
    blacklistedDomains: string[];
    whitelistedSignupDomains?: string[];
    scoredDomains: { [domain: string]: number };
    pricePremiumFactorPerWeekday: { [day: string]: number }; // e.g. { "0": 1.2, "5": 1.1, "6": 1.1 } for weekend premium
    pricePremiumFactorPerWeekOfYear: { [week: string]: number }; // e.g. { "1": 1.2, "52": 1.2 } for new year period
    autoRespondForAssemblageOffers: boolean; // Whether to auto-respond to offers from Assemblage
    scoringWeights: { [component: string]: ScoringWeightConfig };
    autoSearchForDelayedTrucks: boolean; // Whether to auto-search for trucks when bookings are delayed
    drivingHours: { [day: number]: { start: number; end: number } }; // e.g. {1: {start: 7, end: 18}, 2: {start: 8, end: 18}}
    workingCountries?: CountryCode[]; // Countries the tenant wants to work with (used in ANY auto-search mode)
    startNegoSamples: string[]; // email samples to start negotiation
    acceptNegoSamples: string[]; // email samples to accept negotiation
    scoreRecomputeMinutes: number; // Minutes after which offer scores can be recomputed
    language: I18nLang;
    activeSubscriptions: { general?: boolean; autoSearch?: boolean; autoNego?: boolean; mcp?: boolean };
    stripeCustomerId: string;
    negotiationStrategies: TenantNegotiationStrategy[];
    enabledMarketplaces?: MarketplaceNameEnum[];
    bookingFileHtmlTemplate?: string;
    priceSortPolicy: PriceSortPolicy;
    pluginConfig?: PluginConfig;
    unreachableBookingsParams?: {
        enabled: boolean;
        autoWarnManager: boolean;
        autoSearch: boolean;
    };
}
